import {
  Alert,
  Box,
  Card,
  Dialog,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { LoadingButton } from '@mui/lab';
import { FormProvider, RHFSwitch, RHFTextField } from 'src/components/hook-form';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { observer } from 'mobx-react-lite';
import { useSnackbar } from 'notistack';
import CancelIcon from '@mui/icons-material/Cancel';
import useLocales from 'src/hooks/useLocales';
import { useStore } from 'src/stores/store';
import { ApproveDiscound } from 'src/@types/Choqorak/MainTables/Contract/ContractInfo';
import { useEffect, useMemo } from 'react';
import Iconify from 'src/components/Iconify';
import LocalizDatePicker from 'src/sections/common/LocalizDatePicker';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import Label from 'src/components/Label';
import { DateConverter } from 'src/sections/common/DateConverter';
import CalculateDuration from 'src/sections/common/CalculateDuration';
interface Props {
  id: number;
}

export default observer(function ContractInfoDiscount({ id }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { translate } = useLocales();
  const { ContractInfoStore } = useStore();
  const { selectedContractInfoDetail } = ContractInfoStore;
  const validationSchema = Yup.object().shape({
    discountStartDate: Yup.string().required(`${translate('Validation.discountStartDate')}`),
    discountEndDate: Yup.string().required(`${translate('Validation.discountEndDate')}`),
    isApproved: Yup.string().required(`${translate('Validation.isApproved')}`),
  });

  const defaultValues = useMemo<ApproveDiscound>(
    () => ({
      contractId: selectedContractInfoDetail?.contractId,
      isApproved: selectedContractInfoDetail?.isApproved || undefined,
      finalRentPerMonth: selectedContractInfoDetail?.finalRentPerMonth || undefined,
      discountStartDate: selectedContractInfoDetail?.discountStartDate || undefined,
      discountEndDate: selectedContractInfoDetail?.discountEndDate || undefined,
      // attachment: selectedContractInfoDetail?.attachment || undefined,
    }),
    [selectedContractInfoDetail]
  );
  const methods = useForm<any>({
    resolver: yupResolver(validationSchema),
    defaultValues,
  });

  console.log(selectedContractInfoDetail);

  const {
    reset,
    handleSubmit,
    watch,
    setValue,
    control,
    setError,
    formState: { isSubmitting, errors },
  } = methods;

  const onSubmit = (data: ApproveDiscound) => {
    data.discountStartDate = new Date(data.discountStartDate!);
    data.discountEndDate = new Date(data.discountEndDate!);
    ContractInfoStore.ApproveDiscount(data)
      .then(() => {
        ContractInfoStore.setOpenCloseApproveDiscountDialog();
        enqueueSnackbar(`${translate('Tostar.UpdateSuccess')}`);
      })
      .catch((err) => {
        // var json = JSON.parse(err.request.response);
        // if (json.error != null) {
        //   setError('afterSubmit', { ...err, message: json.message });
        // }
      });
  };

  useEffect(() => {
    reset(defaultValues);
  }, [reset, defaultValues]);

  const contractDisCountStartDate = watch('discountStartDate');
  const contractDisCountEndDate = watch('discountEndDate');

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      {!!errors.afterSubmit && (
        <Alert sx={{ mb: 2 }} severity="error">
          {errors.afterSubmit.message}
        </Alert>
      )}
      <Card>
        <Grid container spacing={2}>
          <Grid item xs={6} md={6}>
          
            <Box
              sx={{
                ml: 4,
                mt: 4,
                mb: 4,
              }}
            >
              <Stack spacing={2}>
                <LocalizDatePicker
                  name="discountStartDate"
                  label={translate('contractsInfo.DiscountStartDate')}
                  control={control}
                  showAsterisk={false}
                />
                <LocalizDatePicker
                  name="discountEndDate"
                  label={translate('contractsInfo.DiscountEndDate')}
                  control={control}
                  showAsterisk={false}
                />
                <RHFTextField
                  name="finalRentPerMonth"
                  label={translate('contractsInfo.FinalRentPerMonth')}
                  size="small"
                  disabled
                />
                <Stack spacing={2} sx={{ mb: 2 }}>
                  <>
                    {/* Hidden File Input */}
                    <input
                      type="file"
                      // accept=".jpg"
                      id="contractattachment"
                      onChange={(e) => {
                        const file = e.target.files ? e.target.files[0] : null;
                        setValue('attachment', file);
                      }}
                      style={{ display: 'none' }}
                    />

                    {/* TextField with Attachment Icon */}
                    <TextField
                      label="Attach File"
                      value={watch('attachment')?.name || ''} // Display file name
                      placeholder="No file chosen"
                      InputProps={{
                        readOnly: true, // Prevent manual input
                        startAdornment: (
                          <InputAdornment position="start">
                            {/* Attachment Icon */}
                            <label htmlFor="contractattachment">
                              <IconButton component="span">
                                <AttachFileIcon />
                              </IconButton>
                            </label>
                          </InputAdornment>
                        ),
                      }}
                      fullWidth
                      size="medium"
                      sx={{ m: 1, width: '100%' }}
                    />
                  </>
                </Stack>

                <RHFSwitch
                  name="isApproved"
                  label={translate('contractsInfo.IsApproved')}
                  labelPlacement="end"
                  sx={{ mx: 0, width: 1 }}
                />
              </Stack>
            </Box>
            <Box
              sx={{
                ml: 4,
                mt: 4,
                mb: 4,
              }}
            >
              <Stack direction="row" spacing={3}>
                <LoadingButton
                  type="submit"
                  variant="contained"
                  size="small"
                  color="success"
                  loading={isSubmitting}
                  startIcon={<Iconify icon="eva:edit-fill" />}
                >
                  {translate('CRUD.Save')}
                </LoadingButton>

                <LoadingButton
                  variant="contained"
                  size="small"
                  onClick={() => ContractInfoStore.setOpenCloseApproveDiscountDialog()}
                  startIcon={<CancelIcon />}
                >
                  {translate('CRUD.Cancle')}
                </LoadingButton>
              </Stack>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* اطلاعات قرارداد تخفیف */}
                <Label color="success">
                  <Typography variant="body1" align="center">
                    {translate('contractsInfo.Title')}
                  </Typography>
                </Label>

                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell>{translate('Customer.CustomerName')}</TableCell>
                      <TableCell>{selectedContractInfoDetail?.customerName}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>{translate('Shop.ShopCode')}</TableCell>
                      <TableCell>{selectedContractInfoDetail?.shopCode}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>{translate('contractsInfo.StartDate')}</TableCell>
                      <TableCell>
                        <DateConverter date={selectedContractInfoDetail?.contractStartDate} />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>{translate('contractsInfo.EndDate')}</TableCell>
                      <TableCell>
                        <DateConverter date={selectedContractInfoDetail?.contractEndDate} />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>{translate('contractsInfo.ContractDuration')}</TableCell>
                      <TableCell>
                        {CalculateDuration(
                          translate,
                          selectedContractInfoDetail?.contractStartDate,
                          selectedContractInfoDetail?.contractEndDate
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>{translate('contractsInfo.DurationOfDiscount')}</TableCell>
                      <TableCell>
                        {CalculateDuration(
                          translate,
                          contractDisCountStartDate,
                          contractDisCountEndDate
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Card>
    </FormProvider>
  );
});
